import React from 'react';
import PropTypes from 'prop-types';
import { User } from 'lucide-react';
import {
  FormInput,
  FormSelect,
  DateInput,
  SSNInput,
  SectionHeader,
  FormNavigation,
  FormRow,
} from '../FormComponents';

const suffixOptions = [
  { value: '', label: 'None' },
  { value: 'jr', label: 'Jr.' },
  { value: 'sr', label: 'Sr.' },
  { value: 'ii', label: 'II' },
  { value: 'iii', label: 'III' },
  { value: 'iv', label: 'IV' },
];

const citizenshipOptions = [
  { value: 'us_citizen', label: 'U.S. Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident Alien' },
  { value: 'non_permanent_resident', label: 'Non-Permanent Resident Alien' },
];

const maritalStatusOptions = [
  { value: 'married', label: 'Married' },
  { value: 'separated', label: 'Separated' },
  { value: 'unmarried', label: 'Unmarried (Single, Divorced, Widowed)' },
];

export default function PersonalInfo({ data, onChange, onNext, onBack }) {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  // Calculate max date (must be 18 years old)
  const today = new Date();
  const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate())
    .toISOString()
    .split('T')[0];

  const ssnLength = data.ssn?.length || 0;
  const ssnError = ssnLength > 0 && ssnLength < 9
    ? `SSN must be 9 digits (${ssnLength}/9 entered)`
    : '';

  const isValid =
    Boolean(data.firstName?.trim()) &&
    Boolean(data.lastName?.trim()) &&
    Boolean(data.dateOfBirth) &&
    Boolean(data.ssn?.length === 9) &&
    Boolean(data.citizenship) &&
    Boolean(data.maritalStatus);

  return (
    <div>
      <SectionHeader
        title="Personal Information"
        subtitle="Please provide your legal name as it appears on your ID"
        icon={<User size={28} />}
      />

      <FormRow>
        <FormInput
          label="First Name"
          value={data.firstName || ''}
          onChange={(value) => updateField('firstName', value)}
          placeholder="John"
          required
          autoComplete="given-name"
        />
        <FormInput
          label="Middle Name"
          value={data.middleName || ''}
          onChange={(value) => updateField('middleName', value)}
          placeholder="Robert"
          autoComplete="additional-name"
        />
      </FormRow>

      <FormRow>
        <FormInput
          label="Last Name"
          value={data.lastName || ''}
          onChange={(value) => updateField('lastName', value)}
          placeholder="Smith"
          required
          autoComplete="family-name"
        />
        <FormSelect
          label="Suffix"
          value={data.suffix || ''}
          onChange={(value) => updateField('suffix', value)}
          options={suffixOptions}
          placeholder="Select suffix"
        />
      </FormRow>

      <FormRow>
        <DateInput
          label="Date of Birth"
          value={data.dateOfBirth || ''}
          onChange={(value) => updateField('dateOfBirth', value)}
          required
          max={maxDate}
        />
        <SSNInput
          label="Social Security Number"
          value={data.ssn || ''}
          onChange={(value) => updateField('ssn', value)}
          required
          error={ssnError}
        />
      </FormRow>

      <FormRow>
        <FormSelect
          label="Citizenship Status"
          value={data.citizenship || ''}
          onChange={(value) => updateField('citizenship', value)}
          options={citizenshipOptions}
          placeholder="Select status"
          required
        />
        <FormSelect
          label="Marital Status"
          value={data.maritalStatus || ''}
          onChange={(value) => updateField('maritalStatus', value)}
          options={maritalStatusOptions}
          placeholder="Select status"
          required
        />
      </FormRow>

      <FormNavigation
        onBack={onBack}
        onNext={onNext}
        isValid={isValid}
      />
    </div>
  );
}

PersonalInfo.propTypes = {
  data: PropTypes.shape({
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
    suffix: PropTypes.string,
    dateOfBirth: PropTypes.string,
    ssn: PropTypes.string,
    citizenship: PropTypes.string,
    maritalStatus: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};
